import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AiOutlineCheck } from "react-icons/ai";
import CustomButton from "../../components/common/CustomButton";
import {
  usePaymentSuccessDetails,
  usePaymentSuccessDetailsGuest,
} from "../../custom-hooks/queries";
import { useAuthStore } from "../../store/useAuthStore";

export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tempBookingId = searchParams.get("tempBookingId");

  // ✅ token
  const token = useAuthStore.getState().token;

  // ❗ Hooks always called
  const { data: authData, isLoading: isAuthLoading } = usePaymentSuccessDetails(
    tempBookingId,
    token,
  );

  const { data: guestData, isLoading: isGuestLoading } =
    usePaymentSuccessDetailsGuest(tempBookingId);

  // ✅ decide mode
  const isGuest = !token;
  const isLoading = isGuest ? isGuestLoading : isAuthLoading;

  const bookingDetails = (isGuest ? guestData?.data : authData?.data) || {};

  // ✅ skeleton
  if (isLoading) {
    return <PaymentSuccessSkeleton />;
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-[520px] px-8 py-10">
        {/* SUCCESS ICON */}
        <div className="flex justify-center">
          <div className="w-[90px] h-[90px] rounded-full bg-[#16C784] flex items-center justify-center">
            <AiOutlineCheck size={48} className="text-white" />
          </div>
        </div>

        {/* TITLE */}
        <h2 className="text-2xl font-semibold text-center text-gray-900 mt-6">
          Payment Successful
        </h2>

        <p className="text-center text-gray-600 mt-2">
          Your booking has been confirmed.
        </p>

        {/* DETAILS */}
        <div className="border border-gray-200 rounded-xl mt-8 overflow-hidden">
          <div className="flex justify-end p-4">
            <span className="bg-green-500 text-white text-sm font-semibold px-3 py-1 rounded-md">
              Paid
            </span>
          </div>

          <div className="px-6 pb-6 space-y-4 text-sm">
            <Row label="Booking ID:" value={bookingDetails.bookingCode} />
            <Row label="Experience:" value={bookingDetails.experienceTitle} />
            <Row label="Date:" value={bookingDetails.date} />
            <Row label="Time:" value={bookingDetails.time} />

            <Row
              label={
                bookingDetails?.priceType === "PerGroup"
                  ? "Number of Groups:"
                  : "Number of People:"
              }
              value={
                bookingDetails?.priceType === "PerGroup"
                  ? bookingDetails.group
                  : bookingDetails.people
              }
            />

            {/* ✅ Guest-only extra info */}
            {isGuest && (
              <>
                <Row label="Description:" value={bookingDetails.description} />
                <Row label="Location:" value={bookingDetails.location} />
                <Row label="Vendor:" value={bookingDetails?.vendor?.name} />
              </>
            )}

            <div className="border-t pt-4 mt-4 flex justify-between items-center">
              <span className="text-gray-600">Total Paid:</span>
              <span className="text-2xl font-semibold text-gray-900">
                ${bookingDetails.totalPaid}
              </span>
            </div>
          </div>
        </div>

        {/* ACTIONS */}
        <div className="flex gap-4 mt-8">
          <CustomButton
            variant="secondary"
            className="w-1/2"
            onClick={() => navigate("/")}
          >
            Back to Home
          </CustomButton>

          {!isGuest && (
            <CustomButton
              variant="primary"
              className="w-1/2 bg-[#00BFBF]"
              onClick={() => navigate("/user/my-bookings")}
            >
              View My Bookings
            </CustomButton>
          )}
        </div>
      </div>
    </div>
  );
}

/* ===========================
   Skeleton Loader
=========================== */
function PaymentSuccessSkeleton() {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-[520px] px-8 py-10 animate-pulse">
        <div className="flex justify-center">
          <div className="w-[90px] h-[90px] rounded-full bg-gray-200" />
        </div>

        <div className="h-6 bg-gray-200 rounded w-2/3 mx-auto mt-6" />
        <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto mt-3" />

        <div className="border border-gray-200 rounded-xl mt-8 overflow-hidden">
          <div className="flex justify-end p-4">
            <div className="h-6 w-16 bg-gray-200 rounded" />
          </div>

          <div className="px-6 pb-6 space-y-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex justify-between gap-4">
                <div className="h-4 bg-gray-200 rounded w-1/3" />
                <div className="h-4 bg-gray-200 rounded w-1/4" />
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-4 mt-8">
          <div className="h-12 bg-gray-200 rounded w-1/2" />
          <div className="h-12 bg-gray-200 rounded w-1/2" />
        </div>
      </div>
    </div>
  );
}

/* ===========================
   Reusable Row
=========================== */
function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-900 font-medium text-right">
        {value || "-"}
      </span>
    </div>
  );
}
